import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { CSPUrlsMap } from '../../routes/CSPUrlsMap';
import { RenewalAdjustCover } from '../../components/renewal/adjust/renewalAdjustCover';
import RenewalBanner from '../../components/renewal/banner/renewalBanner';
import { getErrorBannerDescription, getErrorBannerDescriptionTitle } from '../../components/renewal/banner/banners';
import { BrandContext } from '../../contextProviders/brandContext';
import { FeatureFlagContext } from '../../contextProviders/featureFlagContext';
import { FaqsBlock } from '../../components/helpCentre/faqsBlock/faqsBlock';
import { transformCMSRichText, transformContent } from '../../utils/transformContent';
import { ActionResult } from '../../components/actionResult/actionResult';
import { useRenewal } from './useRenewal';
import { introductionText, renewalText } from './fallbackContent';
import Loading from '../../components/shared/Loading/Loading';
import { getGtmService } from '../../services/gtmService/GtmService';
import { ERRORS } from '../../components/renewal/consts';
import { AdjustedRenewalSummary } from '../../components/renewal/summary/adjustedRenewalSummary';
import { RenewalSummary } from '../../components/renewal/summary/renewalSummary';
import { getClaimingExample } from '../../components/renewal/summary/summary';
import {
  useGetFaqContentQuery,
  useGetWhatsCoveredGenericQuery,
  useGetGeneralTooltipContentQuery,
  useGetBannerContentQuery,
  useGetCmsTagsQuery
} from '../../data/kontentAi';
import {
  FAQ_CODENAME,
  RENEWAL_HUB_CONTENT,
  RENEWAL_HUB_MODULAR_CONTENT,
  RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP
} from '../../constants/cmsCodeName';
import './renewal.scss';

export const Renewal = () => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const { adjustmentPolicyContent, renewalBannerContainer } = useOutletContext() || {};
  const navigate = useNavigate();
  const {
    renewal,
    isMobile,
    adjustedSummary,
    renewalSummaryData,
    renewalSummaryIsLoading,
    renewalSummaryIsError,
    renewalQuoteData,
    renewalQuoteIsLoading,
    handleSubmitRenewalAdjustment,
    renewalEligibilityIsLoading,
    newPremiumAnnual,
    auth0AccessToken,
    policyNumber,
    renewalEligibilityIsError,
    renewalQuoteIsError,
    submitRenewalIsError,
    unableToSubmit
  } = useRenewal();

  const routerLocation = useLocation();
  const entrySource = routerLocation.state?.source ?? 'direct';
  const entrySourcePage = routerLocation.state?.sourcePage ?? null;

  // Fires once the page has a renewable policy — so failed eligibility loads are not entries.
  const entryTrackedRef = useRef(false);
  useEffect(() => {
    if (entryTrackedRef.current) return;
    if (!policyNumber || !renewal || !renewal.CanRenewPolicy) return;
    entryTrackedRef.current = true;
    getGtmService().pushRenewalHubEntry({ policyNumber, entrySource, entrySourcePage });
  }, [policyNumber, renewal, entrySource, entrySourcePage]);

  // CSP-3380: the quote error renders inline rather than short-circuiting the page, so a
  // rendered page is not necessarily a usable one — this carries that distinction separately
  // from renewal_hub_entry above.
  const unavailableTrackedRef = useRef(false);
  useEffect(() => {
    if (unavailableTrackedRef.current || !renewalQuoteIsError || !policyNumber) return;
    unavailableTrackedRef.current = true;
    getGtmService().pushAdjustCoverUnavailable({ policyNumber, reason: 'quote_error' });
  }, [renewalQuoteIsError, policyNumber]);

  const onSubmitRenewalAdjustment = () => {
    if (isSubmitDisabled) {
      return;
    }

    setIsSubmitDisabled(true);

    // getClaimingExample does arithmetic on both inputs, so a missing one yields NaN rather than
    // undefined - which withoutAbsentValues cannot filter. Reported as a pair or not at all.
    const canDeriveClaimExample =
      adjustedSummary?.BenefitPercentage !== undefined && adjustedSummary?.Excess !== undefined;
    const claimExample = canDeriveClaimExample
      ? getClaimingExample({
          BenefitPercentage: adjustedSummary.BenefitPercentage,
          Excess: adjustedSummary.Excess
        })
      : undefined;

    const currentAnnual = renewalSummaryData?.PremiumAnnual ?? 0;
    const nextAnnual = adjustedSummary?.newPremiumAnnual ?? 0;
    const premiumDirection =
      // eslint-disable-next-line no-nested-ternary
      nextAnnual > currentAnnual ? 'increase' : nextAnnual < currentAnnual ? 'decrease' : 'unchanged';

    getGtmService().pushAdjustCoverSubmit({
      policyNumber,
      excess: adjustedSummary?.Excess,
      benefitPercentage: adjustedSummary?.BenefitPercentage,
      routineCare: !!adjustedSummary?.RoutineCare,
      boosterCare: !!adjustedSummary?.BoosterCare,
      annualBenefitLimit: renewalSummaryData?.AnnualBenefitLimit,
      newPremium: adjustedSummary?.newPremium,
      newPremiumAnnual: nextAnnual,
      premiumDirection,
      claimExampleBill: claimExample?.billAmount,
      claimExampleWePay: claimExample?.wePay,
      isCoverAdjusted: !!adjustedSummary?.isCoverAdjusted
    });

    try {
      handleSubmitRenewalAdjustment();
    } catch (error) {
      setIsSubmitDisabled(false);
      throw error;
    }
  };

  const renewalSummary = (
    <RenewalSummary
      renewalData={renewalSummaryData}
      isLoading={renewalSummaryIsLoading}
      isError={renewalSummaryIsError}
    />
  );

  const { activeBrandContext } = useContext(BrandContext);
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;

  const redirectToDashboard = () => {
    navigate(CSPUrlsMap.dashboard.url);
  };

  const { data: faqsData } = useGetFaqContentQuery(
    {
      brandPartner: activeBrandContext.brand.key,
      faqCodeName: FAQ_CODENAME.ADJUST_POLICY,
      previewEnabled: isKontentAiPreviewEnabled
    },
    { skip: !auth0AccessToken }
  );
  const { data: whatsCoveredContent } = useGetWhatsCoveredGenericQuery(
    {
      previewEnabled: isKontentAiPreviewEnabled,
      element: [RENEWAL_HUB_CONTENT.BOOSTER_CARE_CONTENT, RENEWAL_HUB_CONTENT.ROUTINE_CARE_CONTENT]
    },
    { skip: !auth0AccessToken || !policyNumber }
  );
  const { data: generalTooltip } = useGetGeneralTooltipContentQuery({
    previewEnabled: isKontentAiPreviewEnabled,
    element: [
      RENEWAL_HUB_CONTENT.EXCESS_TOOLTIP,
      RENEWAL_HUB_CONTENT.BENEFIT_PERCENTAGE_TOOLTIP,
      RENEWAL_HUB_CONTENT.BOOSTER_CARE_TOOLTIP,
      RENEWAL_HUB_CONTENT.ROUTINE_CARE_TOOLTIP
    ]
  });
  const { data: brandTags } = useGetCmsTagsQuery({
    brandPartner: activeBrandContext.brand.key,
    previewEnabled: isKontentAiPreviewEnabled
  });
  const { data: bannerContentData } = useGetBannerContentQuery(
    { previewEnabled: isKontentAiPreviewEnabled },
    { skip: !renewalQuoteIsError || !auth0AccessToken }
  );

  const cmsTags = {
    brandpartner_phone: brandTags?.brandpartner_phone
  };
  const hasFaqData = faqsData && Object.keys(faqsData).length > 0;
  const introductionContent =
    adjustmentPolicyContent?.content?.[RENEWAL_HUB_CONTENT.INTRODUCTION_CONTENT]?.[
      RENEWAL_HUB_MODULAR_CONTENT.INTRODUCTION
    ] || introductionText;
  const renewalSummaryContent =
    adjustmentPolicyContent?.content?.[RENEWAL_HUB_CONTENT.CHANGE_SUMMARY]?.[
      RENEWAL_HUB_MODULAR_CONTENT.CHANGE_SUMMARY
    ] || renewalText;
  const adjustRenewalContentData = {
    ...renewalSummaryData,
    csp_BenefitPercentage: adjustedSummary?.BenefitPercentage,
    csp_Excess: adjustedSummary?.Excess,
    csp_NewPremium: newPremiumAnnual,
    csp_IncludeOrExclude_RoutineCare: adjustedSummary?.RoutineCare ? 'Include' : 'Exclude',
    csp_IncludeOrExclude_BoosterCare: adjustedSummary?.BoosterCare ? 'Include' : 'Exclude',
    ...cmsTags
  };

  if (renewalEligibilityIsLoading) {
    return <Loading />;
  }

  if (renewalEligibilityIsError) {
    return (
      <ActionResult
        description={ERRORS.LOADING_ERROR}
        showIcon={false}
        hasFaqContent
        faqCodename={FAQ_CODENAME.ADJUST_POLICY}
        handleActionRedirection={redirectToDashboard}
      />
    );
  }

  if (submitRenewalIsError) {
    return (
      <ActionResult
        description={ERRORS.SUBMIT_ERROR}
        showIcon={false}
        hasFaqContent
        faqCodename={FAQ_CODENAME.ADJUST_POLICY}
        handleActionRedirection={redirectToDashboard}
      />
    );
  }

  if (!policyNumber || !renewal) {
    return (
      <ActionResult
        description={ERRORS.POLICY_NOT_VALID}
        showIcon={false}
        hasFaqContent
        faqCodename={FAQ_CODENAME.ADJUST_POLICY}
        handleActionRedirection={redirectToDashboard}
      />
    );
  }
  if (!renewal.CanRenewPolicy) {
    return (
      <ActionResult
        title="We've received your renewal"
        descriptionTitle="Thanks for keeping your pet protected!"
        description="If you would like to make any further updates, please contact our Customer Service team."
        showIcon
        hasFaqContent
        buttonText="Back to My Pets"
        faqCodename={FAQ_CODENAME.CONFIRMATION}
        handleActionRedirection={() => navigate(CSPUrlsMap.myPets.url)}
      />
    );
  }
  if (unableToSubmit) {
    return (
      <ActionResult
        title="We've received your renewal"
        descriptionTitle="Thanks for keeping your pet protected!"
        description="If you would like to make any further updates, please contact our Customer Service team."
        showIcon
        hasFaqContent
        buttonText="Back to My Pets"
        faqCodename={FAQ_CODENAME.CONFIRMATION}
        handleActionRedirection={() => navigate(CSPUrlsMap.myPets.url)}
      />
    );
  }

  const adjustedRenewalSummary = (
    <AdjustedRenewalSummary
      renewalData={renewalSummaryData}
      isLoading={renewalSummaryIsLoading}
      cmsData={adjustmentPolicyContent?.content}
    />
  );

  let renewalAdjustContent = renewalSummaryIsLoading || renewalQuoteIsLoading ? <Loading /> : null;

  if (renewalQuoteIsError) {
    renewalAdjustContent = (
      <ActionResult
        descriptionTitle={getErrorBannerDescriptionTitle(bannerContentData, brandTags)}
        description={
          (brandTags && getErrorBannerDescription(bannerContentData, brandTags)) || ERRORS.QAPI_NOT_AVAILABLE
        }
        showIcon
        handleActionRedirection={redirectToDashboard}
      />
    );
  } else if (renewalSummaryData && !renewalSummaryIsLoading && renewalQuoteData && !renewalQuoteIsLoading) {
    renewalAdjustContent = (
      <RenewalAdjustCover
        renewalSummaryData={renewalSummaryData}
        renewalQuoteData={renewalQuoteData}
        cmsData={adjustmentPolicyContent?.content}
        whatsCoveredContent={whatsCoveredContent}
        generalTooltip={generalTooltip}
      />
    );
  }

  return (
    <>
      {renewalQuoteIsError &&
        renewalBannerContainer &&
        createPortal(
          <RenewalBanner
            type="full"
            policyNumber={policyNumber}
            isError
          />,
          renewalBannerContainer
        )}
      <h1 className="mainPageHeader">
        {transformCMSRichText(
          introductionContent?.heading,
          'Pet renewal',
          renewalSummaryData,
          RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP
        )}
      </h1>
      <main>
        <div className="renewalIntroduction">
          {transformCMSRichText(
            introductionContent?.value,
            'Introduction text',
            renewalSummaryData,
            RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP
          )}
        </div>

        {isMobile && renewalSummary}

        {renewalAdjustContent}

        {adjustedSummary?.isCoverAdjusted && (
          <div className="renewalStatement">
            {transformContent(
              renewalSummaryContent?.value,
              'Renewal summary',
              adjustRenewalContentData,
              RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP,
              adjustmentPolicyContent?.content?.[RENEWAL_HUB_CONTENT.CHANGE_SUMMARY]
            )}
            <button
              type="button"
              className="button primary"
              onClick={onSubmitRenewalAdjustment}
              disabled={isSubmitDisabled}
            >
              <span>Submit</span>
            </button>
          </div>
        )}
        {hasFaqData && (
          <FaqsBlock
            questionSet={faqsData?.FAQs?.questions}
            heading={{ text: 'FAQs' }}
            onQuestionToggle={({ question, isOpen }) =>
              getGtmService().pushFaqInteraction({
                policyNumber,
                faqQuestion: question,
                interactionType: isOpen ? 'open' : 'close',
                pageName: 'adjust_cover'
              })
            }
          />
        )}
      </main>
      <aside>
        {!isMobile && renewalSummary}
        {!isMobile && adjustedSummary?.isCoverAdjusted && adjustedRenewalSummary}
      </aside>
    </>
  );
};
